import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { LinkDetail, LinkInput, SiteSummary, createDraftLink } from '../../models/cms-models';
import { LinksService } from '../../services/links-service';
import { SiteContextService } from '../../services/site-context.service';
import { TagsService } from '../../services/tags-service';
import { isValidAbsoluteHttpUrl, removeTrackingParameters } from '../../utils/url';
import { getCanonicalYouTubeUrl } from '../../utils/youtube-url';
import { TagSelectorComponent } from '../../components/tag-selector/tag-selector';

@Component({
  selector: 'app-link-editor-page',
  imports: [CommonModule, FormsModule, RouterLink, TagSelectorComponent],
  templateUrl: './link-editor-page.html',
  styleUrl: './link-editor-page.css',
})
export class LinkEditorPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private linksService = inject(LinksService);
  private siteContext = inject(SiteContextService);
  private tagsService = inject(TagsService);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  link: LinkDetail | null = null;
  siteId: string | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  isCreateMode = false;
  tagSuggestions: string[] = [];
  readonly ratingOptions = [
    { value: -1, label: 'None' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
    { value: 8, label: '8' },
    { value: 9, label: '9' },
    { value: 10, label: '10' },
  ];
  private activeRouteKey: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.route.paramMap, this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, sites, activeSiteId]) => {
        void this.loadRouteState(params.get('linkId'), sites, activeSiteId);
      });
  }

  get pageTitle(): string {
    if (this.link?.headline?.trim()) {
      return this.link.headline;
    }

    return this.isCreateMode ? 'New link' : 'Link';
  }

  get siteName(): string | null {
    return this.sites.find((site) => site.id === this.siteId)?.name ?? null;
  }

  get cleanedYouTubeUrl(): string | null {
    const trimmedUrl = this.link?.url.trim() ?? '';
    if (!trimmedUrl) {
      return null;
    }

    const canonicalUrl = getCanonicalYouTubeUrl(trimmedUrl);
    return canonicalUrl && canonicalUrl !== trimmedUrl ? canonicalUrl : null;
  }

  get cleanedTrackingUrl(): string | null {
    const trimmedUrl = this.link?.url.trim() ?? '';
    if (!trimmedUrl) {
      return null;
    }

    const cleanedUrl = removeTrackingParameters(trimmedUrl);
    return cleanedUrl && cleanedUrl !== trimmedUrl ? cleanedUrl : null;
  }

  get hasValidUrl(): boolean {
    return isValidAbsoluteHttpUrl(this.link?.url ?? '');
  }

  get canSubmitLink(): boolean {
    const trimmedHeadline = this.link?.headline?.trim() ?? '';

    return this.hasValidUrl && !!trimmedHeadline;
  }

  async saveLink(): Promise<void> {
    if (!this.link || !this.canSubmitLink) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      if (this.isCreateMode) {
        const created = await this.linksService.createLink(this.toInput(this.link));
        this.link = created;
        this.siteId = created.siteId;
        this.siteContext.setActiveSite(created.siteId);
      } else {
        const updated = await this.linksService.updateLink(this.link.id, this.toInput(this.link));
        this.link = updated;
        this.siteId = updated.siteId;
        this.siteContext.setActiveSite(updated.siteId);
      }

      await this.router.navigate(['/links']);
    } catch {
      this.error = this.isCreateMode ? 'Failed to create the link.' : 'Failed to save the link.';
    } finally {
      this.saving = false;
    }
  }

  async deleteLink(): Promise<void> {
    if (!this.link || !this.siteId) {
      return;
    }

    const confirmed = window.confirm('Delete this link? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      await this.linksService.deleteLink(this.link.id);
      this.siteContext.setActiveSite(this.siteId);
      void this.router.navigate(['/links']);
    } catch {
      this.error = 'Failed to delete the link.';
    } finally {
      this.saving = false;
    }
  }

  cleanYouTubeUrl(): void {
    if (!this.link || !this.cleanedYouTubeUrl) {
      return;
    }

    this.link.url = this.cleanedYouTubeUrl;
  }

  cleanTrackingUrl(): void {
    if (!this.link || !this.cleanedTrackingUrl) {
      return;
    }

    this.link.url = this.cleanedTrackingUrl;
  }

  private async loadRouteState(linkId: string | null, sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.loading = false;
      this.link = null;
      this.siteId = null;
      this.tagSuggestions = [];
      return;
    }

    const resolvedSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!resolvedSiteId) {
      return;
    }

    const routeKey = linkId ? `link:${linkId}` : `new:${resolvedSiteId}`;

    if (this.activeRouteKey === routeKey) {
      return;
    }

    this.activeRouteKey = routeKey;
    this.loading = true;
    this.error = null;

    try {
      if (linkId) {
        this.isCreateMode = false;
        const link = await this.linksService.getLink(linkId);
        this.link = link;
        this.siteId = link.siteId;
        this.tagSuggestions = await this.tagsService.getSiteTags(link.siteId);
        this.siteContext.setActiveSite(link.siteId);
      } else {
        this.isCreateMode = true;
        this.siteId = resolvedSiteId;
        this.link = createDraftLink(resolvedSiteId);
        this.tagSuggestions = await this.tagsService.getSiteTags(resolvedSiteId);
        this.siteContext.setActiveSite(resolvedSiteId);
      }
    } catch {
      this.activeRouteKey = null;
      this.error = linkId ? 'Failed to load the link.' : 'Failed to prepare a new link.';
      this.link = null;
      this.tagSuggestions = [];
    } finally {
      this.loading = false;
    }
  }

  private toInput(link: LinkDetail): LinkInput {
    return {
      siteId: link.siteId,
      url: link.url.trim(),
      description: link.description.trim(),
      headline: link.headline?.trim() || null,
      rating: Number(link.rating),
      tags: [...link.tags],
    };
  }
}
